'use client';
import React, { useCallback, useRef, useState } from 'react';
import { ChevronDown, Sparkles, HelpCircle } from 'lucide-react';
import SectionShell from './ui/Section';
import { SectionHeader } from './ui/SectionHeader';
import { clsx } from 'clsx';

const FAQS = [
  {
    question: 'What is ZeroClub?',
    answer:
      'ZeroClub is a community for entrepreneurs, creators, and ambitious professionals.\nWe bring people together through events to create a space for growth, connection, and new opportunities.',
  },
  {
    question: 'In which countries does ZeroClub operate?',
    answer:
      'The club is actively expanding across Europe. We currently host events in the Czech Republic and Austria.',
  },
  {
    question: 'What types of events do you host?',
    answer:
      'We organize expert talks, business dinners, hands-on workshops, informal networking nights, and local community activations.\nEach event is designed to spark valuable connections, real conversations, and fresh ideas.',
  },
  {
    question: 'How can I make the most of your events as an entrepreneur?',
    answer:
      'Get involved: introduce yourself, join discussions, offer value to others, share resources, and grow your network while helping others grow theirs.\nZeroClub is built on real exchange — not just business cards.',
  },
  {
    question: 'How can I attend an event?',
    answer:
      "Click the Join now button in the top-right corner of the site and fill in the short form.\nYou'll receive a confirmation email with event details and payment instructions.",
  },
  {
    question: 'How much does it cost to attend?',
    answer:
      'Pricing depends on the format.\nTickets typically range from 500 to 700 CZK, with exact amounts listed on each event page.',
  },
  {
    question: 'What language are the events in?',
    answer:
      'All events are held in English to welcome participants from different countries and backgrounds.',
  },
  {
    question: 'How can I become a speaker at ZeroClub?',
    answer:
      "If you have a strong topic or specialize in fields like finance, marketing, sales, tech, or AI — we'd love to hear from you.\nVisit the Partner with ZeroClub section on our website and fill out the speaker form.",
  },
] as const;

type FAQItemProps = {
  index: number;
  faq: typeof FAQS[number];
  isOpen: boolean;
  onToggle: (index: number) => void;
};

const FAQItem = React.memo(function FAQItem({
  index,
  faq,
  isOpen,
  onToggle,
}: FAQItemProps) {
  const contentRef = useRef<HTMLDivElement>(null);

  return (
    <article className="faq-item group">
      <div
        className={clsx(
          'relative rounded-3xl overflow-hidden transition-all duration-500 bg-gradient-to-br',
          isOpen
            ? 'from-[#0d273d]/30 via-[#1a3b5c]/22 to-[#d65c30]/26'
            : 'from-[#0d273d]/18 via-[#1a3b5c]/14 to-[#d65c30]/16'
        )}
      >
        {/* Glow */}
        <div
          className="pointer-events-none absolute -inset-2 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700"
          style={{
            background:
              'radial-gradient(60% 60% at 30% 0%, rgba(13,39,61,0.25), rgba(26,59,92,0.20) 50%, rgba(214,92,48,0.15) 85%, transparent)',
            filter: 'blur(18px)',
          }}
        />

        {/* Card */}
        <div
          className={clsx(
            'relative bg-white/[0.04] backdrop-blur-2xl border-2 transition-all duration-500',
            isOpen
              ? 'border-[#d65c30]/35 shadow-xl shadow-[#d65c30]/15'
              : 'border-white/[0.06] group-hover:border-white/[0.12]'
          )}
          style={{ borderRadius: 'inherit' }}
        >
          {/* Header */}
          <button
            aria-expanded={isOpen}
            aria-controls={`faq-panel-${index}`}
            onClick={() => onToggle(index)}
            className="w-full px-6 sm:px-8 py-6 sm:py-7 flex items-center gap-4 sm:gap-5 text-left"
          >
            {/* Icon */}
            <div className="flex-shrink-0">
              <div
                className={clsx(
                  'w-11 h-11 rounded-xl flex items-center justify-center ring-2 transition-all duration-500',
                  isOpen
                    ? 'bg-gradient-to-br from-[#d65c30] via-[#e8743f] to-[#ff9b59] ring-[#d65c30]/50 shadow-lg shadow-[#d65c30]/35'
                    : 'bg-[#d65c30]/10 ring-[#d65c30]/20 group-hover:ring-[#d65c30]/40'
                )}
              >
                <HelpCircle
                  className={clsx(
                    'w-6 h-6 transition-colors duration-300',
                    isOpen ? 'text-white' : 'text-[#d65c30]'
                  )}
                />
              </div>
            </div>

            {/* Question */}
            <h3
              className={clsx(
                'flex-1 font-montserrat font-bold text-lg sm:text-xl transition-colors duration-300',
                isOpen ? 'text-white' : 'text-slate-100 group-hover:text-white'
              )}
            >
              {faq.question}
            </h3>

            {/* Arrow */}
            <ChevronDown
              className={clsx(
                'w-6 h-6 transition-all duration-500',
                isOpen
                  ? 'rotate-180 text-[#d65c30]'
                  : 'text-slate-400 group-hover:text-[#d65c30]/80'
              )}
            />
          </button>

          {/* Content */}
          <div
            id={`faq-panel-${index}`}
            className={clsx(
              'grid transition-[grid-template-rows] duration-500 ease-in-out',
              isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
            )}
          >
            <div className="overflow-hidden">
              <div
                ref={contentRef}
                className="px-6 sm:px-8 pb-7 pl-[4.5rem] sm:pl-[5.25rem]"
              >
                <div className="h-[2px] bg-[#d65c30] mb-5 rounded-full" />
                <p className="font-montserrat text-slate-300 leading-relaxed text-[15px] whitespace-pre-line">
                  {faq.answer}
                </p>
              </div>
            </div>
          </div>

          {/* Accent line */}
          {isOpen && (
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#d65c30]/55 to-transparent" />
          )}
        </div>
      </div>
    </article>
  );
});

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const handleToggle = useCallback((i: number) => {
    setOpenIndex((curr) => (curr === i ? null : i));
  }, []);

  return (
    <SectionShell id="faq" bgClass="bg-navy-top">


      {/* Header */}
      <SectionHeader
        eyebrowText="FAQ"
        eyebrowIcon={HelpCircle}
        title="Frequently Asked Questions"
        subtitle="Everything you need to know before joining us"
        textColorClass="text-white"
        align="center"
        titleGradientClassName="bg-gradient-to-r from-[#0d273d] via-[#1a3b5c] to-[#d65c30]"
      />

      {/* FAQ ITEMS */}
      <div className="relative z-10 max-w-4xl mx-auto mt-12 sm:mt-16">
        <div className="space-y-5">
          {FAQS.map((faq, index) => (
            <FAQItem
              key={index}
              index={index}
              faq={faq}
              isOpen={openIndex === index}
              onToggle={handleToggle}
            />
          ))}
        </div>

        {/* CTA */}
        <div className="mt-14 text-center">
          <div className="group inline-flex items-center gap-3 px-7 py-3.5 rounded-full bg-gradient-to-r from-white/[0.04] to-white/[0.08] backdrop-blur-lg border border-white/10 hover:border-[#d65c30]/35 transition-all duration-300 hover:scale-[1.02]">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#d65c30]/25 via-[#ea7a3c]/30 to-[#ff9b59]/35 flex items-center justify-center shadow-md shadow-[#d65c30]/20">
              <Sparkles className="w-4 h-4 text-[#ff9b59]" />
            </div>

            <p className="text-sm text-slate-200 font-montserrat">
              Still have questions?{' '}
              <a
                href="#contact"
                className="font-bold text-white underline decoration-[#d65c30]/50 hover:decoration-[#d65c30] hover:text-[#d65c30] transition-colors"
              >
                Contact our team
              </a>{' '}
              — we're here to help.
            </p>
          </div>
        </div>
      </div>
    </SectionShell>
  );
}